import {
  calculateSparkReceipt,
  Coupon,
  formatCurrency,
  SparkDiscount,
} from "@greenflagdate/shared";
import { Box, BoxProps } from "@chakra-ui/react";
import { useMemo } from "react";

interface SparkInvoiceProps extends BoxProps {
  title: string;
  price: number;
  coupon?: Coupon;
  discount?: SparkDiscount | null;
}

export const SparkInvoice = ({
  title,
  price,
  coupon,
  discount: sparkDiscount,
  ...props
}: SparkInvoiceProps) => {
  // Same calculation the API charges through, so the quoted total is the
  // amount that actually hits the card. This used to tax the pre-coupon
  // amount locally and overstated the total by the tax on the coupon.
  const receipt = useMemo(
    () => calculateSparkReceipt(price, { discount: sparkDiscount, coupon }),
    [price, sparkDiscount, coupon]
  );
  return (
    // The rows are unlabelled amounts, so there is no role or accessible name
    // for a test to target and the same figures appear elsewhere on the
    // checkout screen. Hence the test ids — see spark-register.spec.ts.
    <Box
      fontSize="0.75rem"
      paddingY="1rem"
      paddingX="0.25rem"
      display="flex"
      flexDirection="column"
      gap="0.5rem"
      width="100%"
      data-testid="spark-invoice"
      {...props}
    >
      <Box display="flex" justifyContent="space-between">
        <Box>{title}</Box>
        <Box display="flex" gap="0.5rem" alignItems="center">
          {receipt.discount && (
            <Box
              textDecoration="line-through"
              color="brand.gray.primary"
              data-testid="spark-invoice-list-price"
            >
              {formatCurrency(receipt.listPrice)}
            </Box>
          )}
          <Box data-testid="spark-invoice-price">
            {formatCurrency(receipt.listPrice - (receipt.discount?.amount ?? 0))}
          </Box>
        </Box>
      </Box>
      {receipt.coupon && (
        <Box
          display="flex"
          justifyContent="space-between"
          color="brand.green.primary"
        >
          <Box>{receipt.coupon.description}</Box>
          <Box data-testid="spark-invoice-coupon">
            -{formatCurrency(receipt.coupon.amount)}
          </Box>
        </Box>
      )}
      <Box display="flex" justifyContent="space-between">
        <Box>Tax</Box>
        <Box data-testid="spark-invoice-tax">{formatCurrency(receipt.tax)}</Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        borderTop="1px solid black"
      >
        <Box>Total</Box>
        <Box data-testid="spark-invoice-total">
          {formatCurrency(receipt.total)}
        </Box>
      </Box>
    </Box>
  );
};
